import React from 'react';
import { format } from 'date-fns';
import { ArrowUpCircle, ArrowDownCircle, Clock, CheckCircle, XCircle, AlertCircle, Loader2, Activity } from 'lucide-react';
import SectionBand from '@/components/ui/SectionBand';

const STATUS_CONFIG = {
  pending: {
    icon: Clock,
    color: 'text-brand-400',
    bgColor: 'bg-brand-400/10',
    label: 'Pending'
  },
  posted: {
    icon: CheckCircle,
    color: 'text-green-400',
    bgColor: 'bg-green-400/10',
    label: 'Complete'
  },
  failed: {
    icon: XCircle,
    color: 'text-red-400',
    bgColor: 'bg-red-400/10',
    label: 'Failed'
  },
  reversed: {
    icon: AlertCircle,
    color: 'text-orange-400',
    bgColor: 'bg-orange-400/10',
    label: 'Reversed'
  }
};

const TYPE_CONFIG = {
  credit: {
    icon: ArrowUpCircle,
    color: 'text-green-400',
    sign: '+'
  },
  debit: {
    icon: ArrowDownCircle,
    color: 'text-red-400',
    sign: '-'
  },
  adjustment: {
    icon: AlertCircle,
    color: 'text-brand-400',
    sign: ''
  },
  hold: {
    icon: Clock,
    color: 'text-brand-400',
    sign: ''
  },
  release: {
    icon: CheckCircle,
    color: 'text-green-400',
    sign: ''
  }
};

export default function ReserveLedgerTable({ entries, isLoading }) {
  const formatCurrency = (cents) => {
    const absValue = Math.abs(cents);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(absValue / 100);
  };

  // Reusable shell so the loading / empty / populated states share the same
  // SectionBand chrome and don't render mismatched headers (which would shift
  // the layout when state transitions).
  const ledgerShell = (children) => (
    <div className="rounded-2xl sm:rounded-3xl bg-white/[0.06] backdrop-blur-xl border border-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.03)_inset] overflow-hidden">
      <SectionBand
        icon={Activity}
        iconColor="text-brand-300"
        accentRgb="167,139,250"
        title="Activity"
        rightSlot={
          entries && entries.length > 0 ? (
            <span className="text-[11px] text-white/45 font-normal tabular-nums px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
              {entries.length}
            </span>
          ) : null
        }
      />
      <div className="p-4 sm:p-8">{children}</div>
    </div>
  );

  if (isLoading) {
    return ledgerShell(
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return ledgerShell(
      <div className="text-center py-12">
        <Clock className="w-12 h-12 mx-auto text-white/20 mb-4" />
        <p className="text-white/60 font-normal">No activity yet</p>
        <p className="text-white/40 text-sm mt-1">Add funds to get started</p>
      </div>
    );
  }

  // Compute running balance for each entry (only posted entries affect balance)
  // Entries come newest-first; sort chronologically, compute cumulative, then map back
  const balanceMap = (() => {
    const chronological = [...entries].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const map = new Map();
    let running = 0;
    for (const entry of chronological) {
      if (entry.status === 'posted') {
        running += entry.amount_cents;
      }
      map.set(entry.id, running);
    }
    return map;
  })();

  return ledgerShell(
    <>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-white/40 font-normal text-sm py-3 px-2">Date</th>
              <th className="text-left text-white/40 font-normal text-sm py-3 px-2">Description</th>
              <th className="text-right text-white/40 font-normal text-sm py-3 px-2">Amount</th>
              <th className="text-right text-white/40 font-normal text-sm py-3 px-2">Balance</th>
              <th className="text-right text-white/40 font-normal text-sm py-3 px-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const statusConfig = STATUS_CONFIG[entry.status] || STATUS_CONFIG.pending;
              const typeConfig = TYPE_CONFIG[entry.type] || TYPE_CONFIG.credit;
              const StatusIcon = statusConfig.icon;
              const TypeIcon = typeConfig.icon;
              const runningBalance = balanceMap.get(entry.id) ?? 0;
              
              return (
                <tr 
                  key={entry.id} 
                  className="border-b border-white/5 hover:bg-white/5 transition-colors"
                >
                  <td className="py-4 px-2">
                    <div className="text-white/80 text-sm">
                      {format(new Date(entry.created_at), 'MMM d, yyyy')}
                    </div>
                    <div className="text-white/40 text-xs">
                      {format(new Date(entry.created_at), 'h:mm a')}
                    </div>
                  </td>
                  <td className="py-4 px-2">
                    <div className="flex items-center gap-2">
                      <TypeIcon className={`w-4 h-4 ${typeConfig.color}`} />
                      <span className="text-white/85">{entry.description}</span>
                    </div>
                  </td>
                  <td className="py-4 px-2 text-right">
                    <span className={`font-medium ${entry.amount_cents >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {entry.amount_cents >= 0 ? '+' : ''}{formatCurrency(entry.amount_cents)}
                    </span>
                  </td>
                  <td className="py-4 px-2 text-right">
                    <span className="text-white/60 font-normal text-sm">{formatCurrency(runningBalance)}</span>
                  </td>
                  <td className="py-4 px-2 text-right">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${statusConfig.bgColor} ${statusConfig.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusConfig.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {entries.map((entry) => {
          const statusConfig = STATUS_CONFIG[entry.status] || STATUS_CONFIG.pending;
          const typeConfig = TYPE_CONFIG[entry.type] || TYPE_CONFIG.credit;
          const StatusIcon = statusConfig.icon;
          const TypeIcon = typeConfig.icon;
          const runningBalance = balanceMap.get(entry.id) ?? 0;
          
          return (
            <div 
              key={entry.id} 
              className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <TypeIcon className={`w-3.5 h-3.5 flex-shrink-0 ${typeConfig.color}`} />
                  <p className="text-white/85 text-xs truncate">{entry.description}</p>
                </div>
                <span className={`text-xs font-normal flex-shrink-0 ${entry.amount_cents >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {entry.amount_cents >= 0 ? '+' : ''}{formatCurrency(entry.amount_cents)}
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <p className="text-white/30 text-xs">
                  {format(new Date(entry.created_at), 'MMM d, yyyy • h:mm a')}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-white/30 text-xs">Bal: {formatCurrency(runningBalance)}</span>
                  <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] ${statusConfig.bgColor} ${statusConfig.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    {statusConfig.label}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
